import { Rol } from '../value-objects/Rol';

export interface PerfilProps {
  id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  rol: Rol;
  clienteId: number | null;
  activo: boolean;
  bloqueado: boolean;
  debeCambiarClave: boolean;
  puedeUsarActuadores: boolean;
  puedeVerUbicaciones: boolean;
  /** Clientes adicionales a los que el usuario tiene acceso, más allá del propio. */
  clientesAcceso: readonly number[];
  /** Áreas concretas a las que se restringe el acceso; vacío = todas las de su alcance. */
  areasAcceso: readonly number[];
}

/**
 * Perfil del usuario autenticado.
 * Responde qué es el usuario; qué puede hacer lo decide `PoliticaAcceso`.
 */
export class Perfil {
  constructor(private readonly props: PerfilProps) {}

  get id(): string {
    return this.props.id;
  }
  get nombre(): string {
    return this.props.nombre?.trim() || this.props.email || 'Usuario';
  }
  get email(): string | null {
    return this.props.email;
  }
  get telefono(): string | null {
    return this.props.telefono;
  }
  get rol(): Rol {
    return this.props.rol;
  }
  get clienteId(): number | null {
    return this.props.clienteId;
  }
  get activo(): boolean {
    return this.props.activo;
  }
  get bloqueado(): boolean {
    return this.props.bloqueado;
  }
  get debeCambiarClave(): boolean {
    return this.props.debeCambiarClave;
  }
  get puedeUsarActuadores(): boolean {
    return this.props.puedeUsarActuadores;
  }
  get puedeVerUbicaciones(): boolean {
    return this.props.puedeVerUbicaciones;
  }
  get areasAcceso(): readonly number[] {
    return this.props.areasAcceso;
  }

  /** Iniciales para el avatar, derivadas del nombre visible. */
  get iniciales(): string {
    const partes = this.nombre.split(/\s+/).filter(Boolean);
    const primera = partes.at(0);
    if (!primera) return '?';

    const ultima = partes.at(-1);
    if (partes.length === 1 || !ultima) return primera.slice(0, 2).toUpperCase();

    return (primera.charAt(0) + ultima.charAt(0)).toUpperCase();
  }

  /** El usuario puede iniciar sesión y operar. */
  get puedeAcceder(): boolean {
    return this.props.activo && !this.props.bloqueado;
  }

  /** Motivo por el que se le niega el acceso, en lenguaje para el usuario. */
  get motivoAccesoDenegado(): string | null {
    if (this.props.bloqueado) return 'Tu cuenta está bloqueada. Contacta al administrador.';
    if (!this.props.activo) return 'Tu cuenta está inactiva. Contacta al administrador.';
    return null;
  }

  /**
   * Clientes visibles para este usuario.
   * `null` significa "todos" (superadministrador, sin restricción de cliente).
   */
  get clientesVisibles(): number[] | null {
    if (this.props.rol.esSuperadmin) return null;
    const ids = new Set<number>(this.props.clientesAcceso);
    if (this.props.clienteId !== null) ids.add(this.props.clienteId);
    return [...ids];
  }

  /** `true` si el usuario puede ver datos del cliente indicado. */
  alcanzaCliente(clienteId: number | null | undefined): boolean {
    if (this.props.rol.esSuperadmin) return true;
    if (clienteId === null || clienteId === undefined) return false;
    return (this.clientesVisibles ?? []).includes(clienteId);
  }

  /** `true` si el usuario puede ver datos del área indicada. */
  alcanzaArea(areaId: number | null | undefined): boolean {
    if (this.props.rol.esSuperadmin) return true;
    if (this.props.areasAcceso.length === 0) return true; // sin restricción explícita
    if (areaId === null || areaId === undefined) return false;
    return this.props.areasAcceso.includes(areaId);
  }
}
